import React from "react";
import { formatDistanceToNow } from "date-fns";
import { getQueryStyle } from "../FormCategories";

const QueriesTable = ({
  data,
  loading,
  selectedIds,
  setSelectedIds,
  onRowClick,
  onAction,
}) => {
  // --- Bulk Selection Logic ---
  const handleSelectAll = (e) => {
    setSelectedIds(e.target.checked ? data.map((q) => q.id) : []);
  };

  const handleSelectRow = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
  };

  // --- Utility: Status & Priority Styling ---
  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === "pending") return "bg-amber-500";
    if (s === "resolved") return "bg-green-500";
    if (s === "unresolved") return "bg-red-500";
    return "bg-blue-500";
  };

  const getPriorityStyle = (priority) => {
    const p = priority?.toLowerCase();
    if (p === "high" || p === "p0")
      return "text-red-600 bg-red-50 ring-1 ring-red-100";
    return "text-blue-600 bg-blue-50 ring-1 ring-blue-100";
  };

  return (
    <div className="bg-white rounded-2xl lg:rounded-[24px] border border-gray-200 shadow-sm overflow-hidden min-h-[320px] lg:min-h-[500px] flex flex-col">
      {/* Mobile cards */}
      <div className="md:hidden flex-1 divide-y divide-slate-100">
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="p-4 animate-pulse">
              <div className="h-16 bg-slate-100 rounded-2xl" />
            </div>
          ))
        ) : data.length === 0 ? (
          <div className="py-20 text-center text-slate-400 font-bold uppercase tracking-widest text-xs">
            No Inquiries Found
          </div>
        ) : (
          data.map((query) => {
            const badgeStyle = getQueryStyle(query.formCategory);
            const isUnread = query.status === "pending";
            return (
              <div
                key={query.id}
                onClick={() => onRowClick(query)}
                className={`p-4 active:bg-slate-50 transition-colors ${
                  isUnread ? "bg-blue-50/40 border-l-4 border-l-blue-500" : ""
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-slate-800 text-white flex items-center justify-center text-xs font-black shrink-0">
                    {query.sender?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p
                          className={`text-sm truncate ${isUnread ? "font-black text-[#0B1C2D]" : "font-bold text-slate-700"}`}
                        >
                          {query.sender || "Unknown"}
                        </p>
                        <p className="text-[11px] text-slate-400 truncate">
                          {query.email}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${getPriorityStyle(query.priority)}`}
                      >
                        {query.priority}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-lg text-[9px] font-black uppercase ${badgeStyle.color}`}
                      >
                        {query.formCategory}
                      </span>
                      <span className="text-[11px] font-bold text-slate-500 truncate">
                        {query.formTitle}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2 h-2 rounded-full ${getStatusColor(query.status)}`}
                        />
                        <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                          {query.status}
                        </span>
                      </div>
                      <div
                        className="flex gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => onAction("reply", query.id)}
                          className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-[#0B1C2D] hover:text-white rounded-xl active:scale-90"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            reply
                          </span>
                        </button>
                        <button
                          onClick={() => onAction("delete", query.id)}
                          className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-red-500 hover:text-white rounded-xl active:scale-90"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            delete
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto flex-1">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50/50 text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100">
            <tr>
              <th className="px-6 py-5 w-12 text-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-[#0B1C2D] focus:ring-[#0B1C2D] cursor-pointer"
                  checked={
                    data.length > 0 && selectedIds.length === data.length
                  }
                  onChange={handleSelectAll}
                  disabled={loading || data.length === 0}
                />
              </th>
              <th className="px-6 py-5">Sender</th>
              <th className="px-6 py-5">Subject</th>
              <th className="px-6 py-5">Priority</th>
              <th className="px-6 py-5">Status</th>
              <th className="px-6 py-5 text-right">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td colSpan="6" className="px-6 py-6 bg-gray-50/20" />
                </tr>
              ))
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan="6"
                  className="px-6 py-20 text-center text-slate-400 font-bold uppercase tracking-widest"
                >
                  No Inquiries Found
                </td>
              </tr>
            ) : (
              data.map((query) => {
                const badgeStyle = getQueryStyle(query.formCategory);
                const isUnread = query.status === "pending";

                return (
                  <tr
                    key={query.id}
                    onClick={() => onRowClick(query)}
                    className={`group transition-all cursor-pointer border-l-4 ${
                      isUnread
                        ? "bg-blue-50/20 border-l-blue-500"
                        : "hover:bg-slate-50 border-l-transparent"
                    } ${selectedIds.includes(query.id) ? "bg-blue-50/40" : ""}`}
                  >
                    <td
                      className="px-6 py-4 text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-[#0B1C2D] focus:ring-[#0B1C2D] cursor-pointer"
                        checked={selectedIds.includes(query.id)}
                        onChange={(e) => handleSelectRow(query.id, e)}
                      />
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-slate-800 text-white flex items-center justify-center text-xs font-black">
                          {query.sender?.charAt(0).toUpperCase() || "?"}
                        </div>
                        <div className="flex flex-col">
                          <span
                            className={`text-sm tracking-tight ${isUnread ? "font-black text-[#0B1C2D]" : "font-bold text-slate-600"}`}
                          >
                            {query.sender || "Unknown"}
                          </span>
                          <span className="text-[11px] text-slate-400 font-medium">
                            {query.email}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-lg text-[9px] font-black uppercase w-fit ${badgeStyle.color}`}
                        >
                          {query.formCategory}
                        </span>
                        <span className="text-[11px] font-bold text-slate-500 truncate max-w-[200px]">
                          {query.formTitle}
                        </span>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${getPriorityStyle(query.priority)}`}
                      >
                        {query.priority}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span
                          className={`w-2 h-2 rounded-full ${getStatusColor(query.status)}`}
                        />
                        <span className="text-[11px] font-black uppercase text-slate-400 tracking-widest">
                          {query.status}
                        </span>
                      </div>
                      <p className="text-[9px] text-slate-300 font-medium mt-1">
                        {query.submittedAt
                          ? formatDistanceToNow(new Date(query.submittedAt), {
                              addSuffix: true,
                            })
                          : "Recently"}
                      </p>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div
                        className="flex justify-end gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => onAction("reply", query.id)}
                          className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-[#0B1C2D] hover:text-white rounded-xl transition-all"
                          title="Reply via Email"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            reply
                          </span>
                        </button>

                        <button
                          onClick={() => onAction("delete", query.id)}
                          className="w-9 h-9 flex items-center justify-center text-slate-400 hover:bg-red-500 hover:text-white rounded-xl transition-all"
                          title="Delete"
                        >
                          <span className="material-symbols-outlined text-[18px]">
                            delete
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default QueriesTable;
